/* eslint-disable react/no-children-prop */
import React from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation, Trans } from "react-i18next";
//
import {
  SpringOnboardingText,
  SpringOnboardingTitle,
} from "@components/Typography";
import {
  Alert,
  Anchor,
  Box,
  Flex,
  Group,
  Stack,
  SimpleGrid,
} from "@mantine/core";
import { useColors } from "@utils/useColors";
import { useAppForm } from "@components/form/app-form";
//
import { staticLinks } from "@config/staticLinks";
import { US_STATES } from "@neuromotion-inc/mightier-ts-sdk";

import {
  useSpringOnboardingStore,
  SpringAccountCreateFormSchema,
} from "src/services/spring-health-onboarding-store";

const Route = getRouteApi("/spring-health/register");

export function SpringHealthUserCreatePage() {
  const { t } = useTranslation();
  const themeColors = useColors();

  const searchParams = Route.useSearch();

  const nav = Route.useNavigate();

  const cachePayloadToStorage = useSpringOnboardingStore(
    (store) => store.setPayload,
  );

  const inputStyles = React.useMemo(() => {
    return {
      input: { color: themeColors.royal100 },
    };
  }, [themeColors.royal100]);

  const form = useAppForm({
    // NOTE: for simplicity, we clear this store upon landing on the spring health register
    // sign up page (/spring-health/register/sign-up/). This means that refreshing the page DOES clear the form!
    // So no need to get initial values from storage or anything.
    defaultValues: {
      firstName: "",
      lastName: "",
      emailOptIn: true,
      phoneNum: "",
      //
      addr1: "",
      addr2: "",
      city: "",
      provinceCode: "",
      zip: "",
      country: "US",
      agreedToTos: false,
    },
    validators: {
      onSubmit: SpringAccountCreateFormSchema,
      onBlur: (val) => {
        console.log(val.value);
      },
    },
    onSubmit: async ({ value }) => {
      console.log("SUBMITTED - HERE IS THE VALUE", value);
      cachePayloadToStorage({ userCreate: value });
      nav({
        to: "/spring-health/register/enroll",
        search: searchParams,
      });
    },
  });

  return (
    <Stack px="lg" mt={{ xs: 0, md: "xl" }}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <SimpleGrid cols={{ xs: 1, md: 2 }} spacing="xl">
          <Stack>
            <Alert color="blue" p="xl" radius={16}>
              <SpringOnboardingText>
                <Trans
                  i18nKey="springHealth.register.eligibilityConfirmed"
                  components={{ b: <b /> }}
                />
              </SpringOnboardingText>
            </Alert>

            <Box>
              <SpringOnboardingTitle order={3}>
                {t("springHealth.register.contact.title")}
              </SpringOnboardingTitle>
            </Box>

            <form.AppField
              name="firstName"
              children={(field) => {
                return (
                  <field.TextField
                    placeholder={t("springHealth.register.contact.firstName")}
                    errors={field.state.meta.errors}
                    autoComplete="given-name"
                    data-testid="first-name"
                    styles={inputStyles}
                  />
                );
              }}
            />

            <form.AppField
              name="lastName"
              children={(field) => {
                return (
                  <field.TextField
                    placeholder={t("springHealth.register.contact.lastName")}
                    errors={field.state.meta.errors}
                    autoComplete="family-name"
                    data-testid="last-name"
                    styles={inputStyles}
                  />
                );
              }}
            />

            <form.AppField
              name="emailOptIn"
              children={(field) => {
                return (
                  <field.Checkbox
                    label={t("springHealth.register.contact.emailOptIn")}
                    c={themeColors.royal100}
                  />
                );
              }}
            />

            <form.AppField
              name="phoneNum"
              children={(field) => {
                return (
                  <field.PhoneNumber
                    label={t("springHealth.register.contact.phoneNumber")}
                    labelProps={{ c: themeColors.royal100 }}
                    styles={inputStyles}
                  />
                );
              }}
            />
          </Stack>

          <Stack>
            <Box>
              <SpringOnboardingTitle order={3}>
                {t("springHealth.register.shipping.title")}
              </SpringOnboardingTitle>
              <SpringOnboardingText>
                {t("springHealth.register.shipping.note")}
              </SpringOnboardingText>
            </Box>
            <form.AppField
              name="addr1"
              children={(field) => {
                return (
                  <field.TextField
                    placeholder={t("springHealth.register.shipping.address")}
                    trim={false}
                    errors={field.state.meta.errors}
                    autoComplete="address-line1"
                    data-testid="addr-addr1"
                    styles={inputStyles}
                  />
                );
              }}
            />
            <form.AppField
              name="addr2"
              children={(field) => {
                return (
                  <field.TextField
                    placeholder={t(
                      "springHealth.register.shipping.addressLine2",
                    )}
                    errors={field.state.meta.errors}
                    autoComplete="address-line2"
                    data-testid="addr-addr2"
                    styles={inputStyles}
                  />
                );
              }}
            />

            <Group>
              <form.AppField
                name="city"
                children={(field) => {
                  return (
                    <field.TextField
                      placeholder={t("springHealth.register.shipping.city")}
                      errors={field.state.meta.errors}
                      autoComplete="address-level2"
                      data-testid="addr-city"
                      styles={inputStyles}
                    />
                  );
                }}
              />
              <form.AppField
                name="provinceCode"
                children={(field) => {
                  return (
                    <field.Select
                      placeholder={t("springHealth.register.shipping.state")}
                      data={Object.entries(US_STATES).map((entry) => {
                        return { label: entry[1], value: entry[0] };
                      })}
                      value={field.state.value}
                      onChange={(newVal) => field.handleChange(newVal)}
                      onBlur={field.handleBlur}
                      autoComplete="address-level2"
                      data-testid="addr-provinceCode"
                      styles={inputStyles}
                    />
                  );
                }}
              />
            </Group>

            <form.AppField
              name="zip"
              children={(field) => {
                return (
                  <field.TextField
                    placeholder={t("springHealth.register.shipping.zipCode")}
                    errors={field.state.meta.errors}
                    autoComplete="postal-code"
                    data-testid="addr-zip"
                    styles={inputStyles}
                  />
                );
              }}
            />

            <form.AppField
              name="country"
              children={(field) => {
                return (
                  <field.Select
                    label={t("springHealth.register.shipping.country")}
                    labelProps={{ c: themeColors.royal100 }}
                    data={["US"]}
                    value={field.state.value}
                    onChange={(newVal) => field.handleChange(newVal)}
                    onBlur={field.handleBlur}
                    disabled
                  />
                );
              }}
            />

            <form.AppField
              name="agreedToTos"
              children={(field) => (
                <field.Checkbox
                  label={
                    <Trans
                      i18nKey="springHealth.register.contact.agreedToTos"
                      components={{
                        1: (
                          <Anchor
                            href={staticLinks.termsOfService}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: "inherit" }}
                            c={themeColors.tufts100}
                          />
                        ),
                      }}
                    />
                  }
                  error={field.state.meta.errors[0]?.message}
                  data-testid="agreed-to-tos"
                  c={themeColors.royal100}
                />
              )}
            />
          </Stack>
        </SimpleGrid>

        <Flex direction="column" align="flex-end" mt="xl" pr="xl" gap="sm">
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.isSubmitting,
              state.isDefaultValue,
            ]}
            children={([canSubmit, isSubmitting, isDefaultValue]) => (
              <form.SubmitButton
                loading={isSubmitting}
                disabled={!canSubmit || isDefaultValue}
                data-testid="submit-btn"
              >
                {t("loginPage.next")}
              </form.SubmitButton>
            )}
          />
        </Flex>
      </form>
    </Stack>
  );
}
